import constate from "constate";
import {
	type ComponentType,
	type FC,
	type PropsWithChildren,
	useState,
} from "react";
import { useEventCallback } from "usehooks-ts";
import { AlertScope, AlertType } from "../alert/Alert";

interface ModalData<T extends object> {
	component: ComponentType<T>;
	props: PropsWithChildren<T>;
}

const [ModalContextProvider, useModalContext, useModalStack] = constate(
	() => {
		// biome-ignore lint/suspicious/noExplicitAny: Modal state needs to accept any component props
		const [stack, setStack] = useState<ModalData<any>[]>([]);

		const open = useEventCallback(
			<T extends object>(component: ComponentType<T>, props: T) => {
				setStack((prev) => [...prev, { component, props }]);
			},
		);

		const close = useEventCallback(() => setStack((prev) => prev.slice(0, -1)));

		const state = stack.length > 0 ? stack[stack.length - 1] : undefined;

		return { state, stack, open, close };
	},
	(value) => ({
		state: value.state,
		open: value.open,
		close: value.close,
	}),
	(value) => ({ stack: value.stack }),
);

const ModalStack: FC = () => {
	const { stack } = useModalStack();

	return (
		<>
			{stack.map((entry, index) => {
				const Modal = entry.component;
				return (
					// biome-ignore lint/suspicious/noArrayIndexKey: stack identity is positional
					<AlertScope key={index} filter={[AlertType.ERROR]}>
						<Modal {...entry.props} />
					</AlertScope>
				);
			})}
		</>
	);
};

export const ModalContext: FC<PropsWithChildren> = (props) => {
	return (
		<ModalContextProvider>
			{props.children}
			<ModalStack />
		</ModalContextProvider>
	);
};

export { useModalContext };
